import { z } from "zod";
import { paymentProviderTypeEnum } from "./payment";

// =============================================================================
// PAYMENT PROVIDER ACCOUNT SCHEMAS
// =============================================================================

// Payment Provider Account Schema (for responses - masked credentials)
export const paymentProviderAccountSchema = z.object({
  id: z.string(),
  providerType: paymentProviderTypeEnum,

  // Stripe fields (masked in responses)
  stripeAccountId: z.string().optional().nullable(),
  stripePublishableKey: z.string().optional().nullable(),
  stripeLivemode: z.boolean().optional().nullable(),

  // PayPal fields (masked in responses)
  paypalMerchantId: z.string().optional().nullable(),
  paypalMode: z.string().optional().nullable(),

  isActive: z.boolean(),
  isDefault: z.boolean(),
  connectedAt: z.date(),

  organizationId: z.string(),
  userId: z.string(),
  createdAt: z.date(),
  updatedAt: z.date(),
});

export type PaymentProviderAccountSchema = z.infer<typeof paymentProviderAccountSchema>;

// =============================================================================
// OAUTH CALLBACK SCHEMAS
// =============================================================================

// Stripe OAuth Callback Schema
// Note: When Stripe returns an error, it includes `error` and `error_description` instead of `code`
export const stripeOAuthCallbackSchema = z.object({
  code: z.string().optional(),
  state: z.string().optional(),
  error: z.string().optional(),
  error_description: z.string().optional(),
});

export type StripeOAuthCallbackSchema = z.infer<typeof stripeOAuthCallbackSchema>;

// PayPal OAuth Callback Schema
export const paypalOAuthCallbackSchema = z.object({
  code: z.string(),
  state: z.string().optional(),
});

export type PayPalOAuthCallbackSchema = z.infer<typeof paypalOAuthCallbackSchema>;

// OAuth State Schema (stored temporarily)
export const oauthStateSchema = z.object({
  organizationId: z.string(),
  userId: z.string(),
  redirectUrl: z.string().optional(),
  createdAt: z.number(), // Unix timestamp
});

export type OAuthStateSchema = z.infer<typeof oauthStateSchema>;

// =============================================================================
// INVOICE PAYMENT TOKEN SCHEMAS
// =============================================================================

// Create Invoice Payment Token Schema
export const createInvoicePaymentTokenSchema = z.object({
  invoiceId: z.string().uuid(),
});

export type CreateInvoicePaymentTokenSchema = z.infer<typeof createInvoicePaymentTokenSchema>;

// Invoice Payment Token Schema (response)
export const invoicePaymentTokenSchema = z.object({
  id: z.string(),
  token: z.string(),
  invoiceId: z.string(),
  organizationId: z.string(),
  expiresAt: z.date(),
  isRevoked: z.boolean(),
  createdAt: z.date(),
  updatedAt: z.date(),
});

export type InvoicePaymentTokenSchema = z.infer<typeof invoicePaymentTokenSchema>;

// Payment Link Response Schema
export const paymentLinkResponseSchema = z.object({
  token: z.string(),
  paymentUrl: z.string().url(),
  expiresAt: z.date(),
});

export type PaymentLinkResponseSchema = z.infer<typeof paymentLinkResponseSchema>;

// =============================================================================
// PUBLIC PAYMENT SCHEMAS (No Auth Required)
// =============================================================================

// Public Invoice Schema (limited data for public page)
export const publicInvoiceSchema = z.object({
  id: z.string(),
  invoiceNumber: z.string(),
  organizationName: z.string(),
  organizationLogo: z.string().nullable(),
  relationName: z.string(),
  currency: z.string(),
  total: z.number(),
  totalPaid: z.number(),
  amountDue: z.number(),
  dueDate: z.date().nullable(),
  status: z.string(),
  availableProviders: z.array(paymentProviderTypeEnum),
  organizationId: z.string(),
});

export type PublicInvoiceSchema = z.infer<typeof publicInvoiceSchema>;

// Public Payment Init Schema (for initiating payment)
export const publicPaymentInitSchema = z.object({
  token: z.string().min(1),
  amount: z.coerce.number().positive("Amount must be positive"),
  payerEmail: z.string().email("Invalid email address"),
  payerName: z.string().min(1, "Name is required"),
  note: z.string().optional(),
  provider: paymentProviderTypeEnum,
});

export type PublicPaymentInitSchema = z.infer<typeof publicPaymentInitSchema>;

// Stripe Payment Intent Response Schema
export const stripePaymentIntentResponseSchema = z.object({
  clientSecret: z.string(),
  paymentIntentId: z.string(),
  publishableKey: z.string(),
});

export type StripePaymentIntentResponseSchema = z.infer<typeof stripePaymentIntentResponseSchema>;

// PayPal Order Response Schema
export const paypalOrderResponseSchema = z.object({
  orderId: z.string(),
  approvalUrl: z.string().url(),
  clientId: z.string(),
  mode: z.enum(["sandbox", "live"]),
});

export type PayPalOrderResponseSchema = z.infer<typeof paypalOrderResponseSchema>;

// PayPal Capture Schema
export const paypalCaptureSchema = z.object({
  token: z.string(),
  orderId: z.string(),
});

export type PayPalCaptureSchema = z.infer<typeof paypalCaptureSchema>;

// =============================================================================
// DISCONNECT SCHEMA
// =============================================================================

export const disconnectPaymentProviderSchema = z.object({
  id: z.string().uuid(),
});

export type DisconnectPaymentProviderSchema = z.infer<typeof disconnectPaymentProviderSchema>;
